import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { CloseShortButton } from "./CloseShortButton";
import { DocChip } from "../shipment/DocChip";
import { FilePreviewModal } from "../shipment/FilePreviewModal";
import { OrderAttachmentPanel } from "../attachments/OrderAttachmentPanel";
import { DateField } from "../ui/DateField";
import { trpc } from "../../lib/trpc";
import { MaterialNeeds, MaterialNeedsKapi, type MaterialNeedsVeri } from "./MaterialNeeds";

type OrderStatus = "draft" | "confirmed" | "in_production" | "ready" | "shipped" | "cancelled";

const STATUS_BADGE: Record<OrderStatus, { cls: string; label: string }> = {
  draft: { cls: "", label: "Taslak" },
  confirmed: { cls: "b-success", label: "Onaylı" },
  in_production: { cls: "b-warn", label: "Üretimde" },
  ready: { cls: "b-success", label: "Hazır" },
  shipped: { cls: "b-info", label: "Sevk Edildi" },
  cancelled: { cls: "b-danger", label: "İptal" },
};
const STATUS_OPTIONS = Object.keys(STATUS_BADGE) as OrderStatus[];
const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const miniLbl: React.CSSProperties = { fontSize: 11, color: "var(--neutral-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" };

function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtDate(d: string | Date | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

interface Props {
  open: boolean;
  orderId: string | null;
  onClose: () => void;
  onEdit: (id: string) => void;
}

export function OrderDetailDrawer({ open, orderId, onClose, onEdit }: Props) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<null | { title: string; message: ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);

  const orderQ = trpc.order.get.useQuery({ id: orderId! }, { enabled: open && !!orderId });
  const meQ = trpc.auth.me.useQuery();
  // Kalıcı silme yalnız süper-adminde. Bu gizleme bir GÖRÜNÜM kolaylığıdır; asıl kapı
  // uçtaki requireSuperAdmin + "hiç iz yok" kontrolleridir (order.remove).
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;
  const setStatusMut = trpc.order.setStatus.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const removeMut = trpc.order.remove.useMutation({ onSuccess: () => { qc.invalidateQueries(); onClose(); } });
  // Söz verilen teslim tarihi — HER durumda (iptal hariç) düzenlenebilir; ayrı uç.
  const setPromisedMut = trpc.order.setPromisedDate.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const [sozTarih, setSozTarih] = useState("");
  const createFromOrderMut = trpc.workOrder.createFromOrder.useMutation();
  const rereserveMut = trpc.order.rereserve.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const [resvResult, setResvResult] = useState<{ totalReserved: number; totalShortage: number } | null>(null);
  const [prodResult, setProdResult] = useState<{ count: number; codes: string[]; cancelledCount: number; reducedCount: number; overWarnings: string[]; acikWoSayisi: number; skipped: Array<{ itemName: string; reason: string }> } | null>(null);
  const fullProdRef = useRef<HTMLInputElement>(null); // "stoğa rağmen tam üretim" seçimi (onay dialogʼundaki kutu)
  // Çekmece MOUNT KALIR (Orders.tsx koşulsuz basar) → sıfırlanmayan mutation hatası bir
  // sonraki siparişte YANLIŞ gerekçe olarak görünürdü. Hepsi aynı deseni izler.
  useEffect(() => { setProdResult(null); createFromOrderMut.reset(); setResvResult(null); rereserveMut.reset(); removeMut.reset(); setStatusMut.reset(); setPromisedMut.reset(); }, [orderId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const o = orderQ.data;
  const busy = setStatusMut.isPending || createFromOrderMut.isPending || rereserveMut.isPending || removeMut.isPending || setPromisedMut.isPending;
  // Sunucudaki değer: tarih-only, UTC gece yarısında saklanıyor → UTC'den okumak DOĞRU.
  const sozSunucu = o?.promisedDate ? new Date(o.promisedDate).toISOString().slice(0, 10) : "";
  const sozDegisti = sozTarih !== sozSunucu;
  // Sunucudan gelen söz verilen tarihi alana yansıt (kaydetmeden sonra da tazelenir).
  useEffect(() => { setSozTarih(o?.promisedDate ? new Date(o.promisedDate).toISOString().slice(0, 10) : ""); }, [o?.promisedDate, orderId, open]); // `open` ŞART: aynı sipariş yeniden açılınca kaydedilmemiş değer bayat kalırdı

  // Rezervasyonu yenile — durumu değiştirmeden bayat/çapraz rezervasyonları tazeler.
  // Onay istenir: rezervasyon serbest bırakılıp yeniden dağıtıldığı için aynı üründe yarışan
  // başka bir sipariş FEFO'da öne geçebilir (sonuç raporunda eksik olarak görünür).
  const askRereserve = () => {
    if (!o) return;
    setConfirm({
      title: "Rezervasyonu yenile",
      tone: "primary",
      confirmLabel: "Yenile",
      message: (
        <>
          <strong>{o.code}</strong> siparişinin mevcut rezervasyonları bırakılıp güncel stoktan yeniden
          yapılacak. Sipariş durumu, stok hareketleri ve iş emirleri <strong>değişmez</strong>.
          <br />
          Bayat ya da başka siparişe ait lotlara asılı kalmış rezervasyonlar bu işlemle temizlenir.
        </>
      ),
      run: () => {
        setResvResult(null);
        rereserveMut.mutate({ id: o.id }, { onSuccess: (r) => setResvResult({ totalReserved: r.totalReserved, totalShortage: r.totalShortage }) });
      },
    });
  };
  const status = (o?.status ?? "draft") as OrderStatus;
  const sb = STATUS_BADGE[status];
  const lines = o?.lines ?? [];
  const editable = status === "draft" || status === "confirmed";

  const [targetStatus, setTargetStatus] = useState<OrderStatus>("draft");
  useEffect(() => { if (o) setTargetStatus(o.status as OrderStatus); }, [o?.id, o?.status]);

  // Para birimine göre alt toplam + TRY karşılığı genel toplam
  const byCurrency: Record<string, number> = {};
  let tryTotal = 0;
  let anyForeign = false;
  for (const l of lines) {
    if (l.unitPrice == null || !l.currency) continue;
    const lt = l.qty * l.unitPrice;
    byCurrency[l.currency] = (byCurrency[l.currency] ?? 0) + lt;
    tryTotal += lt * (l.exchangeRate ?? 1);
    if (l.currency !== "TRY") anyForeign = true;
  }
  const currencyKeys = Object.keys(byCurrency);

  // Silme reddi EN ÖNDE: kullanıcı butona yeni bastığı için beklediği geri bildirim odur.
  const mutErr = removeMut.error?.message ?? setPromisedMut.error?.message ?? setStatusMut.error?.message ?? createFromOrderMut.error?.message ?? rereserveMut.error?.message ?? null;

  // Kalıcı sil — yalnız iptal edilmiş sipariş. Uç, ardında iz (parti/lot/sevkiyat/cari)
  // bırakmış siparişi zaten reddeder ve izin NEREDE olduğunu söyler; buradaki onay kutusu
  // işlemin GERİ ALINAMAZ olduğunu açıkça bildirir.
  function askRemove() {
    if (!o) return;
    setConfirm({
      title: "Siparişi kalıcı olarak sil?",
      message: (
        <>
          <div><b>{o.code}</b> ve satırları veritabanından <b>kalıcı olarak</b> silinecek. Bu işlem geri alınamaz.</div>
          <div style={{ marginTop: 8, fontSize: 13, color: "var(--neutral-500)" }}>
            Siparişin üretim partisi, lotu, sevkiyatı veya cari hesap hareketi varsa silme reddedilir.
          </div>
        </>
      ),
      tone: "danger",
      confirmLabel: "Kalıcı Sil",
      run: () => removeMut.mutate({ id: o.id }),
    });
  }

  function takeToProduction() {
    if (!o) return;
    setConfirm({
      title: "Siparişi üretime al?",
      message: (
        <>
          <div><b>{o.code}</b> için iş emirleri oluşturulup ürünlerin reçetesindeki üretim merkezlerine dağıtılacak.</div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, fontSize: 13, cursor: "pointer" }}>
            <input ref={fullProdRef} type="checkbox" defaultChecked={false} style={{ marginTop: 2 }} />
            <span>Stoğa rağmen <b>tam üretim</b> yap (sipariş miktarının tamamı). İşaretlenmezse yalnızca <b>stok eksiği</b> kadar iş emri açılır.</span>
          </label>
        </>
      ),
      tone: "primary",
      confirmLabel: "Üretime Al",
      run: () => createFromOrderMut.mutate({ orderId: o.id, mode: fullProdRef.current?.checked ? "full" : "shortfall" }, {
        onSuccess: (res) => {
          setProdResult({ count: res.created.length, codes: res.created.map((c) => c.woCode), cancelledCount: res.cancelled.length, reducedCount: res.reduced.length, overWarnings: res.overWarnings, acikWoSayisi: res.acikWoSayisi, skipped: res.skipped.map((s) => ({ itemName: s.itemName, reason: s.reason })) });
          qc.invalidateQueries();
        },
      }),
    });
  }

  function applyStatus() {
    if (!o || targetStatus === status) return;
    if (targetStatus === "cancelled") {
      setConfirm({
        title: "Siparişi iptal et?",
        message: <><b>{o.code}</b> iptal edilecek; rezervasyonlar serbest bırakılır.</>,
        tone: "danger",
        confirmLabel: "İptal Et",
        run: () => setStatusMut.mutate({ id: o.id, status: "cancelled" }),
      });
    } else {
      setStatusMut.mutate({ id: o.id, status: targetStatus });
    }
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        size="3xl"
        title={o ? `Sipariş ${o.code}` : "Sipariş"}
        subtitle={o ? (o.customerName ?? "(Stoğa Üretim)") : undefined}
        footer={
          <>
            {editable ? (
              <button type="button" className="btn-md secondary" style={{ marginRight: "auto" }} onClick={() => o && onEdit(o.id)} disabled={busy}>
                <Icon name="edit" size={15} />Düzenle
              </button>
            ) : null}
            {status === "confirmed" || status === "in_production" || status === "ready" ? (
              <button type="button" className="btn-md secondary" onClick={askRereserve} disabled={busy} title="Mevcut rezervasyonları bırakıp güncel stoktan yeniden yapar (durum değişmez)">
                <Icon name="refresh" size={15} />{rereserveMut.isPending ? "Yenileniyor…" : "Rezervasyonu Yenile"}
              </button>
            ) : null}
            {status === "cancelled" && isSuperAdmin ? (
              <button type="button" className="btn-md danger" onClick={askRemove} disabled={busy} title="İptal edilmiş siparişi veritabanından kalıcı olarak siler (geri alınamaz)">
                <Icon name="trash" size={15} />{removeMut.isPending ? "Siliniyor…" : "Kalıcı Sil"}
              </button>
            ) : null}
            <CloseShortButton order={o} onClosed={onClose} />
            {status === "confirmed" || status === "in_production" ? (
              <button type="button" className="btn-md primary" onClick={takeToProduction} disabled={busy}>
                <Icon name="factory" size={15} />{createFromOrderMut.isPending ? "İş emirleri açılıyor…" : status === "in_production" ? "İş Emirlerini Aç" : "Üretime Al"}
              </button>
            ) : null}
            <button type="button" className="btn-md ghost" onClick={onClose} disabled={busy}>Kapat</button>
          </>
        }
      >
        {!o ? (
          <div style={{ padding: 24, color: "var(--neutral-500)" }}>Yükleniyor…</div>
        ) : (
          <>
            {/* YAPIŞKAN: gövde ayrı kaydırma konteyneri, butonlar ise sabit alt şeritte.
                Aşağı kaydırılmış çekmecede sıradan bir banner ekranın DIŞINDA kalır ve
                reddedilen silme kullanıcıya "hiçbir şey olmadı" gibi görünürdü. */}
            {mutErr ? <div className="form-banner-err" role="alert" style={{ marginBottom: 12, position: "sticky", top: 0, zIndex: 2 }}>{mutErr}</div> : null}
            {/* Eksik sevkle kapatıldıysa gerekçe GÖRÜNÜR olmalı: aksi halde "durum Sevk
                Edildi ama miktarlar tutmuyor" sorusunun cevabı yalnız audit log'da kalır. */}
            {o.closedShortAt ? (
              <div className="form-banner-info" style={{ marginBottom: 12 }}>
                <Icon name="info" size={14} />
                <div>
                  Bu sipariş <b>eksik sevkle kapatıldı</b> ({fmtDate(o.closedShortAt)}) — kalan miktar sevk edilmeyecek.
                  {o.closedShortReason ? <><br />Gerekçe: <b>{o.closedShortReason}</b></> : null}
                </div>
              </div>
            ) : null}
            {resvResult ? (
              <div className={resvResult.totalShortage > 0 ? "form-banner-info" : "form-banner-ok"} style={{ marginBottom: 12 }}>
                Rezervasyon yenilendi — <b className="mono">{fmtQty(resvResult.totalReserved)}</b> birim güncel stoktan rezerve edildi
                {resvResult.totalShortage > 0 ? (
                  <> · <b className="mono">{fmtQty(resvResult.totalShortage)}</b> birim karşılanamadı (stokta yok ya da başka siparişe ayrılmış) — bu kısım üretimden gelmeli.</>
                ) : " · tamamı karşılandı."}
              </div>
            ) : null}
            {prodResult ? (
              <div className={prodResult.count > 0 ? "form-banner-ok" : "form-banner-info"} style={{ marginBottom: 12 }}>
                {prodResult.count > 0 ? (
                  <div>Üretime alındı — <b>{prodResult.count}</b> iş emri oluşturuldu: <span className="mono">{prodResult.codes.join(", ")}</span>. Üretim sayfasındaki merkez sekmelerinden takip edebilirsiniz.</div>
                ) : prodResult.acikWoSayisi > 0 ? (
                  /* Yeni WO açılmadı ÇÜNKÜ zaten açık WO var — "hiçbir şey olmadı" demek yanlış
                     olurdu; sipariş üretime alındı ve kuyrukta görünür (24 Ağu 2026 olayı). */
                  <div>
                    Yeni iş emri gerekmedi — bu siparişin zaten <b>{prodResult.acikWoSayisi}</b> açık iş emri var.
                    Sipariş <b>üretimde</b> olarak işaretlendi; Üretim sayfasındaki merkez sekmelerinden takip edebilirsiniz.
                  </div>
                ) : (
                  <div>Bu siparişten <b>iş emri açılmadı</b>.</div>
                )}
                {prodResult.cancelledCount > 0 ? (
                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--neutral-600)" }}>
                    Sipariş revize edildiği için <b>{prodResult.cancelledCount}</b> eski/mükerrer iş emri iptal edilip güncel miktara göre yeniden oluşturuldu (başlamış üretimler korundu).
                  </div>
                ) : null}
                {prodResult.reducedCount > 0 ? (
                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--neutral-600)" }}>
                    Sipariş azaltıldığı için <b>{prodResult.reducedCount}</b> başlamış iş emrinin (ve alt iş emirlerinin) planı yeni miktara düşürüldü (üretilen korundu).
                  </div>
                ) : null}
                {prodResult.overWarnings.length > 0 ? (
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12, color: "var(--warning-text)" }}>
                    {prodResult.overWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                ) : null}
                {prodResult.skipped.length > 0 ? (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    Açılmayan satırlar:
                    <ul style={{ margin: "3px 0 0", paddingLeft: 18 }}>
                      {prodResult.skipped.map((s, i) => (
                        <li key={i}>{s.itemName} — <span style={{ color: "var(--warning-text)" }}>{s.reason}</span></li>
                      ))}
                    </ul>
                    {prodResult.skipped.some((s) => s.reason === "reçete tanımlı değil") ? (
                      <div style={{ marginTop: 4, color: "var(--neutral-600)" }}>Reçetesi olmayan ürünler için önce Reçeteler sayfasından reçete tanımlayın.</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <span className={`badge-pill ${sb.cls}`}>{sb.label}</span>
              <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>{o.orderType === "customer" ? "Müşteri siparişi" : "Stoğa üretim"}</span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>Durumu değiştir:</span>
              <select className="form-select" value={targetStatus} onChange={(e) => setTargetStatus(e.target.value as OrderStatus)} style={{ width: 148, height: 30, fontSize: 12.5 }} aria-label="Durum">
                {STATUS_OPTIONS.map((s) => {
                  // Üretimde/Hazır/Sevk sistem-tetiklidir (Üretime Al / WO tamamlanması / sevkiyat) — elle seçilemez.
                  const systemDriven = s === "in_production" || s === "ready" || s === "shipped";
                  return <option key={s} value={s} disabled={systemDriven}>{STATUS_BADGE[s].label}</option>;
                })}
              </select>
              <button type="button" className="btn-md primary" style={{ height: 30 }} disabled={busy || targetStatus === status} onClick={applyStatus}>Uygula</button>
            </div>

            <div className="form-row" style={{ gridTemplateColumns: "1fr 1fr 1fr", marginBottom: 6 }}>
              <div><div style={miniLbl}>Müşteri</div><div style={{ fontSize: 13 }}>{o.customerName ?? "—"}</div></div>
              <div><div style={miniLbl}>İstenen Tarih</div><div className="mono" style={{ fontSize: 13 }}>{fmtDate(o.requestedDate)}</div></div>
              {/* SÖZ VERİLEN TESLİM — burada DOĞRUDAN düzenlenir. Sipariş düzenleme ekranı
                  yalnız taslak/onaylıda açılır ve satırları yeniden yaratır; teslim sözü ise
                  üretim/sevkiyat sürerken de değişir. Ayrı uç (order.setPromisedDate) yalnız
                  bu alana dokunur, o yüzden her durumda güvenlidir. */}
              <div>
                <div style={miniLbl}>Söz Verilen Teslim</div>
                {status === "cancelled" ? (
                  <div className="mono" style={{ fontSize: 13 }}>{fmtDate(o.promisedDate)}</div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <DateField
                      value={sozTarih}
                      onChange={setSozTarih}
                      aria-label="Söz verilen teslim tarihi"
                    />
                    {sozDegisti ? (
                      <button
                        type="button"
                        className="btn-md primary sm"
                        disabled={busy}
                        onClick={() => o && setPromisedMut.mutate({ id: o.id, promisedDate: sozTarih ? new Date(sozTarih).toISOString() : null })}
                      >
                        {setPromisedMut.isPending ? "…" : "Kaydet"}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            {o.notes ? <div style={{ marginBottom: 8 }}><div style={miniLbl}>Not</div><div style={{ fontSize: 13 }}>{o.notes}</div></div> : null}

            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginTop: 14, marginBottom: 8, borderTop: "1px solid var(--neutral-100)", paddingTop: 14 }}>
              Satırlar ({lines.length})
            </div>
            <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
              <table className="dt2">
                <thead>
                  <tr>
                    <th>Ürün</th>
                    <th className="num" style={{ width: 90 }}>Miktar</th>
                    <th className="num" style={{ width: 110 }}>Birim Fiyat</th>
                    <th className="num" style={{ width: 120 }}>Tutar</th>
                    <th className="num" style={{ width: 90 }}>Rezerve</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const sym = l.currency ? CUR_SYMBOL[l.currency] ?? l.currency : "";
                    const lt = l.unitPrice != null ? l.qty * l.unitPrice : null;
                    return (
                      <tr key={l.id}>
                        <td>{l.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{l.itemCode}</span></td>
                        <td className="cell-num">{fmtQty(l.qty)}<span className="unit">{l.uom}</span></td>
                        <td className="cell-num">{l.unitPrice != null ? `${fmtMoney(l.unitPrice)} ${sym}` : <span style={{ color: "var(--neutral-400)" }}>—</span>}{l.exchangeRate != null ? <span className="sec mono" style={{ fontSize: 10 }}>kur {fmtMoney(l.exchangeRate)}₺</span> : null}</td>
                        <td className="cell-num">{lt != null ? `${fmtMoney(lt)} ${sym}` : "—"}</td>
                        <td className="cell-num" style={{ color: l.reservedQty > 0 ? "var(--success)" : "var(--neutral-400)" }}>{fmtQty(l.reservedQty)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {currencyKeys.length > 0 ? (
              <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 18, alignItems: "baseline", flexWrap: "wrap" }}>
                {currencyKeys.map((c) => (
                  <span key={c} style={{ fontSize: 13 }}>
                    <span style={{ color: "var(--neutral-500)" }}>Toplam ({c}): </span>
                    <b className="mono">{fmtMoney(byCurrency[c]!)} {CUR_SYMBOL[c] ?? c}</b>
                  </span>
                ))}
                {anyForeign ? (
                  <span style={{ fontSize: 13 }}>
                    <span style={{ color: "var(--neutral-500)" }}>≈ TL karşılığı: </span>
                    <b className="mono">{fmtMoney(tryTotal)} ₺</b>
                  </span>
                ) : null}
              </div>
            ) : null}

            <OrderMaterialNeeds orderId={o.id} acik={open} />
            <OrderShipmentsSection orderId={o.id} open={open} />
            <OrderDocumentsSection orderId={o.id} />
            <OrderProductionSummary orderId={o.id} open={open} />
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        tone={confirm?.tone ?? "primary"}
        confirmLabel={confirm?.confirmLabel ?? "Onayla"}
        cancelLabel="Vazgeç"
        busy={busy}
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}

const LOT_BADGE: Record<string, { cls: string; label: string }> = {
  released: { cls: "b-success", label: "Sevke hazır" },
  pending_qc: { cls: "b-warn", label: "QC bekliyor" },
  on_hold: { cls: "b-warn", label: "Bloke" },
  rejected: { cls: "b-danger", label: "Karantina/Hurda" },
  expired: { cls: "b-danger", label: "SKT geçti" },
  consumed: { cls: "", label: "Tüketildi" },
};
const KIND_TR: Record<string, string> = { stock: "HM", semi: "YM", finished: "MM" };

const SHIP_BADGE: Record<string, { cls: string; label: string }> = {
  draft: { cls: "b-warn", label: "Hazırlanıyor" },
  confirmed: { cls: "b-success", label: "Sevk Edildi" },
  cancelled: { cls: "", label: "İptal" },
};

/** Sipariş sevkiyat bilgileri — taşıyıcı / sürücü / araç / nakliye ücreti / evrak (lot/parti üretim özetinde). */
function OrderShipmentsSection({ orderId, open }: { orderId: string; open: boolean }) {
  const q = trpc.shipment.byOrder.useQuery({ orderId }, { enabled: open });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const ships = q.data ?? [];
  if (q.isLoading || ships.length === 0) return null; // sevkiyat yoksa bölüm gösterilmez

  const kv = (k: string, v: ReactNode) => (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", minWidth: 0 }}>
      <span style={{ minWidth: 96, fontSize: 10.5, fontWeight: 600, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: "0.03em" }}>{k}</span>
      <span style={{ fontSize: 12.5, color: "var(--neutral-800)", minWidth: 0, wordBreak: "break-word" }}>{v || <span style={{ color: "var(--neutral-400)" }}>—</span>}</span>
    </div>
  );

  return (
    <div className="d-card" style={{ marginBottom: 12 }}>
      <div className="d-card-head">
        <div className="left"><span className="t">Sevkiyat Bilgileri</span><span className="s">{ships.length} sevkiyat · taşıyıcı / sürücü / nakliye</span></div>
      </div>
      <div className="d-card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {ships.map((sh) => {
          const sb = SHIP_BADGE[sh.status] ?? { cls: "", label: sh.status };
          const freight = sh.freightParty === "firma"
            ? (sh.freightAmount != null ? <b className="mono">{fmtMoney(sh.freightAmount)} {sh.freightCurrency ?? ""}</b> : "Firmaya ait (tutar girilmedi)")
            : sh.freightParty === "karsi" ? "Karşıya ait" : null;
          const driver = sh.driverName
            ? `${sh.driverName}${sh.driverTc ? ` · TC ${sh.driverTc}` : ""}${sh.driverPhone ? ` · ${sh.driverPhone}` : ""}`
            : null;
          const totals = sh.totals.length ? sh.totals.map((t) => `${fmtQty(t.qty)} ${t.uom}`).join(" + ") : null;
          const clean = sh.vehicleCleanOk == null ? null : sh.vehicleCleanOk ? "Uygun (Evet)" : "Uygun değil (Hayır)";
          const planned = sh.plannedDate ? `${fmtDate(sh.plannedDate)}${sh.plannedTime ? " " + sh.plannedTime : ""}` : null;
          return (
            <div key={sh.id} style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", padding: "10px 12px", background: "var(--neutral-50)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                <span className="cell-id">{sh.code}</span>
                <span className={`badge-pill ${sb.cls}`}>{sb.label}</span>
                {sh.shippedAt ? <span style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>Sevk: <b className="mono">{fmtDate(sh.shippedAt)}</b></span> : null}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 20, rowGap: 6 }}>
                {kv("Taşıyıcı", sh.carrier)}
                {kv("Araç Plaka", sh.vehiclePlate)}
                {kv("Dorse Plaka", sh.trailerPlate)}
                {kv("Sürücü", driver)}
                {kv("İrsaliye No", sh.waybillNo)}
                {kv("Palet / Kat", (sh.palletCount != null || sh.layerCount != null) ? `${sh.palletCount ?? "—"} palet · ${sh.layerCount ?? "—"} kat` : null)}
                {kv("Araç Temizliği", clean)}
                {kv("Planlanan", planned)}
                {kv("Nakliye Ücreti", freight)}
                {kv("Kantar (Dolu / Boş)", (sh.fullWeightKg != null || sh.emptyWeightKg != null)
                  ? `${sh.fullWeightKg != null ? fmtQty(sh.fullWeightKg) : "—"} kg / ${sh.emptyWeightKg != null ? fmtQty(sh.emptyWeightKg) : "—"} kg`
                  : null)}
                {kv("Net Çıkış", sh.netWeightKg != null ? <b className="mono">{fmtQty(sh.netWeightKg)} kg</b> : null)}
                {kv("Miktar", totals ? `${totals} (${sh.lineCount} satır)` : `${sh.lineCount} satır`)}
              </div>
              {sh.documents.length > 0 ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 6 }}>Evraklar ({sh.documents.length})</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {sh.documents.map((url) => <DocChip key={url} url={url} onOpen={setPreviewUrl} />)}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </div>
  );
}

/** Sipariş belgeleri & görselleri — üretim/örnek resmi + sipariş/sevkiyat/sürücü evrakı (izlenebilir toplama + yükleme). */
function OrderDocumentsSection({ orderId }: { orderId: string }) {
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const canEdit = !!meQ.data?.user && (meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).includes("create:order"));
  return (
    <div className="d-card" style={{ marginBottom: 12 }}>
      <div className="d-card-head">
        <div className="left"><span className="t">Belgeler & Görseller</span><span className="s">üretim/örnek resmi · sevkiyat/sürücü evrakı · sipariş belgeleri</span></div>
      </div>
      <div className="d-card-body">
        <OrderAttachmentPanel orderId={orderId} canEdit={canEdit} uploadCategory="order_doc" showCategory />
      </div>
    </div>
  );
}

/**
 * SİPARİŞE ÖZEL MALZEME İHTİYACI (MRP) — kullanıcı isteği (29 Ağu 2026).
 *
 * İSTEK ÜZERİNE çalışır (`enabled: acik`): hesap reçete patlatması + her hammadde için stok
 * ve açık PO sorgusu demek; çekmece her açıldığında kendiliğinden koşmaz.
 *
 * TASLAK SİPARİŞ DE HESAPLANIR: genel MRP ekranı yalnız onaylı ve sonrası siparişleri talep
 * sayar, taslak sipariş orada hiç görünmez — "sipariş aşamasında eksik listesi" isteğinin
 * karşılığı tam olarak budur.
 */
function OrderMaterialNeeds({ orderId, acik }: { orderId: string; acik: boolean }) {
  const [calistir, setCalistir] = useState(false);
  const q = trpc.order.materialNeeds.useQuery({ orderId }, { enabled: acik && calistir, staleTime: 30_000 });
  if (!acik) return null;
  // Henüz çalıştırılmadı: BOŞ TABLO göstermek yanıltıcı olur ("eksik yok" sanılır) — düğme gösterilir.
  if (!calistir) return <MaterialNeedsKapi onCalistir={() => setCalistir(true)} />;
  return (
    <MaterialNeeds
      veri={q.data satisfies MaterialNeedsVeri | undefined}
      yukleniyor={q.isLoading}
      hata={q.error?.message}
      yenile={() => void q.refetch()}
      yenileniyor={q.isFetching}
    />
  );
}

/** Sipariş üretim & maliyet özeti — yönetici özeti: KPI kartları + kümülatif tüketim + üretim partileri (karantina/hurda dahil). */
function OrderProductionSummary({ orderId, open }: { orderId: string; open: boolean }) {
  const q = trpc.order.productionReport.useQuery({ id: orderId }, { enabled: open });
  const r = q.data;
  if (q.isLoading) return <div style={{ marginTop: 18, padding: "12px 0", color: "var(--neutral-500)", fontSize: 13, borderTop: "1px solid var(--neutral-100)" }}>Üretim & maliyet özeti yükleniyor…</div>;
  if (!r) return null;

  const prodUom = r.batches.find((b) => b.itemKind === "finished")?.uom ?? r.batches[0]?.uom ?? "";
  const released = r.byStatus.find((s) => s.status === "released")?.qty ?? 0;
  const lost = r.byStatus.filter((s) => s.status === "rejected" || s.status === "on_hold").reduce((a, s) => a + s.qty, 0);

  return (
    <div style={{ marginTop: 18, borderTop: "1px solid var(--neutral-100)", paddingTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
        <Icon name="factory" size={15} />Üretim Özeti
      </div>

      {r.batchCount === 0 ? (
        <div style={{ padding: "10px 12px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--neutral-500)" }}>
          Bu sipariş için henüz üretim partisi kaydedilmemiş.
        </div>
      ) : (
        <>
          {/* MAMUL ÜRETİMİ YOKKEN AÇIK UYARI: KPI'lar 0 gösterirken altta 13 parti listelenince
              kullanıcı "veri hatalı" sanıyordu (canlıda SO-2026-00037'de bildirildi). Listedeki
              partiler ARA yarı mamullerdir; sipariş ürününden henüz üretim yoktur. */}
          {r.onlySemiProduced ? (
            <div className="form-banner-info" style={{ marginBottom: 12 }}>
              <Icon name="info" size={14} />
              <div>
                Sipariş ürününden <b>henüz üretim yok</b>. Aşağıda listelenen {r.semiBatchCount} parti,
                iş emri zincirinde üretilen <b>ara yarı mamullerdir</b> — bu yüzden "Üretilen" 0 görünüyor.
              </div>
            </div>
          ) : null}
          <div className="dash-kpi-grid" style={{ marginBottom: 14 }}>
            <div className="dash-kpi"><div className="head"><span className="lbl">Üretilen</span></div><div className="val">{fmtQty(r.producedTotal)}<span className="unit">{prodUom}</span></div><div className="sub">{r.batchCount} mamul partisi</div></div>
            <div className="dash-kpi"><div className="head"><span className="lbl">Sevke Hazır</span></div><div className="val" style={{ color: "var(--success)" }}>{fmtQty(released)}<span className="unit">{prodUom}</span></div><div className="sub">QC onaylı</div></div>
            <div className="dash-kpi"><div className="head"><span className="lbl">Karantina / Hurda</span></div><div className="val" style={{ color: lost > 0 ? "var(--danger)" : undefined }}>{fmtQty(lost)}<span className="unit">{prodUom}</span></div><div className="sub">reddedilen</div></div>
          </div>

          <div className="d-card">
            <div className="d-card-head"><div className="left"><span className="t">Üretim Partileri</span><span className="s">{r.batchCount} mamul partisi · karantina/hurda dahil{r.semiBatchCount > 0 ? ` · +${r.semiBatchCount} ara yarı mamul partisi` : ""}</span></div></div>
            <div className="d-card-body flush">
              <table className="dt2">
                <thead><tr><th style={{ width: 150 }}>Parti</th><th>Lot</th><th>Ürün</th><th style={{ width: 120 }}>Durum</th><th className="num" style={{ width: 100 }}>Miktar</th><th style={{ width: 120 }}>İş Emri</th><th style={{ width: 110 }}>Tarih</th></tr></thead>
                <tbody>
                  {r.batches.map((b) => {
                    const lb = b.lotStatus ? LOT_BADGE[b.lotStatus] ?? { cls: "", label: b.lotStatus } : null;
                    return (
                      <tr key={b.code}>
                        <td><span className="cell-id">{b.code}</span></td>
                        <td><span className="mono" style={{ fontSize: 11 }}>{b.lotCode ?? "—"}</span></td>
                        <td><span style={{ fontSize: 9, fontWeight: 600, color: "var(--neutral-500)", background: "var(--neutral-100)", padding: "1px 4px", borderRadius: 3, marginRight: 5 }}>{KIND_TR[b.itemKind] ?? "?"}</span>{b.itemName}</td>
                        <td>{lb ? <span className={`badge-pill ${lb.cls}`} style={{ fontSize: 10.5 }}>{lb.label}</span> : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                        <td className="cell-num">{fmtQty(b.qty)}<span className="unit">{b.uom}</span></td>
                        <td><span className="mono" style={{ fontSize: 10.5, color: "var(--info)" }}>{b.woCode ?? "—"}</span></td>
                        <td><span className="mono" style={{ fontSize: 11, color: "var(--neutral-600)" }}>{fmtDate(b.producedAt)}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
